'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { WordpressPage } from '@benzinga/content-manager';
import ScreenerTemplate from '../../../../src/components/Screener/templates/ScreenerTemplate';
import { MoneyPageTemplate } from '@benzinga/money';
import type { ScreenerSlugProps } from './getScreenerSlugProps';
import { screenerConfigMap } from '../../../../screener-configs/screenerConfigMap';
import { HeroPageHeader } from '../../../_components/LayoutPageHeader/HeroPageHeader';
import { ScreenerPresentedBy } from '../_components/ScreenerPresentedBy';
import { BENZINGA_ORIGIN, normalizeScreenerPath } from '../screenerMeta';
import {
  RANKING_HEADER_BLOCK_NAME,
  getRankingHeaderConfig,
  isRankingVariantHref,
  sortRankingTabs,
} from './rankingHeader';

function withoutRankingHeader(post: WordpressPage): WordpressPage {
  if (!post.header) return post;
  const blocks = post.header.blocks.filter(block => block.blockName !== RANKING_HEADER_BLOCK_NAME);
  return {
    ...post,
    header: blocks.length > 0 ? { ...post.header, blocks } : undefined,
  };
}

export function ScreenerSlugPageContent({ screenerPost, staticSlug, metaProps }: ScreenerSlugProps) {
  const pathname = usePathname();

  if (staticSlug && screenerConfigMap[staticSlug]) {
    const { config } = screenerConfigMap[staticSlug];
    return <ScreenerTemplate key={staticSlug} config={config} />;
  }

  if (screenerPost) {
    const rankingHeader = getRankingHeaderConfig(screenerPost);
    if (rankingHeader && metaProps) {
      const currentPath = normalizeScreenerPath(pathname);
      const canonical = String(metaProps.canonical ?? `${BENZINGA_ORIGIN}${currentPath}`);
      const breadcrumbs = [
        { href: '/tools', id: 'tools', name: 'Tools' },
        { href: '/screener', id: 'screener', name: 'Stock Screeners' },
        { href: currentPath, id: currentPath, name: rankingHeader.title },
      ];
      const serviceTabs = rankingHeader.services
        .map(service => {
          const href = normalizeScreenerPath(service.url);
          return {
            href,
            id: href,
            isActive: currentPath === href,
            label: service.title,
          };
        })
        .filter(tab => tab.href !== '/screener' && isRankingVariantHref(tab.href));
      const tabs = sortRankingTabs(serviceTabs);

      return (
        <>
          <HeroPageHeader
            breadcrumbs={breadcrumbs}
            buttonLocation="screener_page"
            canonical={canonical}
            className="screener-page-header"
            description={rankingHeader.description}
            descriptionFooter={
              <Link className="mt-2 inline-block font-bold text-white hover:underline" href="/screener">
                Use Benzinga&apos;s stock screener to discover more top stocks.
              </Link>
            }
            leadingContent={
              screenerPost.presented_by ? (
                <ScreenerPresentedBy link={screenerPost.presented_by_link} logo={screenerPost.presented_by} />
              ) : undefined
            }
            navAriaLabel="Top stock ranking pages"
            showEdgeBadge
            subtitle={rankingHeader.subtitle}
            tabs={tabs}
            title={rankingHeader.title}
          />
          <MoneyPageTemplate post={withoutRankingHeader(screenerPost)} width="wide" />
        </>
      );
    }

    return <MoneyPageTemplate post={screenerPost} width="wide" />;
  }

  return null;
}
